import type { Trait, LifeChoice, WorldDetail, Dossier, Relationship } from "../types";
import type { NarrativeContext, NarrativeResult } from "../narrative";
import { clamp } from "./helpers";
import {
  fill,
  pick,
  pickDistinct,
  listJoin,
  influenceLead,
  INTEREST_PHRASE,
  STRENGTH_PHRASE,
  AMBITION_PHRASE,
  LIFE_PATH_PHRASE,
} from "./compose";
import { generatePersonName, generateAlienName, generatePlaceName } from "./names";
import { buildUniverseDescription, buildWorldDetails, STRENGTH_TRAIT_MAP } from "./shared";
import {
  TITLE_PREFIXES,
  TITLE_DOMAINS,
  TAGLINE_LEADS,
  TAGLINE_TAILS,
  CIVILIZATION_TYPES,
  TECHNOLOGY_CORES,
  INSTITUTIONAL_FORMS,
  ROLE_DOMAINS_BY_INTEREST,
  OFFICE_BY_LIFEPATH,
  SCALE_PHRASES,
  ORIGIN_SETTINGS,
  ORIGIN_FORMATIVE_EVENTS,
  ORIGIN_AWAKENINGS,
  CRISIS_TYPES,
  TURNING_ACTIONS_BY_AMBITION,
  TURNING_RESOLUTIONS_BY_LIFEPATH,
  TODAY_DUTIES,
  TODAY_INTIMACIES,
  TODAY_HORIZONS,
  CHOICE_EVENTS,
  SKILL_BANKS_BY_INTEREST,
  SKILL_BANKS_BY_LIFEPATH,
  PERSONALITY_TEMPERAMENTS,
  PERSONALITY_HABITS,
  PERSONALITY_CONVICTIONS,
  RELATIONSHIP_MENTORS,
  RELATIONSHIP_RIVALS,
  RELATIONSHIP_COMPANIONS,
  ACHIEVEMENT_POOLS_BY_AMBITION,
  FAILURE_POOLS,
  DILEMMA_PROGRESS_POOLS,
  DILEMMA_TENSION_POOLS,
  TRAJECTORY_NEAR_POOLS,
  TRAJECTORY_LONG_POOLS,
  EXTRA_DETAILS_POOLS,
} from "./ascendant.banks";

/**
 * Ascendant Archetype Generator:
 * Far-future cosmic era, advanced civilization shaped by energy mastery,
 * noospheric coordination, megastructures, and deep-time stewardship.
 * Pure and deterministic, built from recombinable part streams.
 */
export function generateAscendant(ctx: NarrativeContext): NarrativeResult {
  const { name: visitorName, rng, axes, condition, universe, sharedUniverse, resolved } = ctx;

  // Stream helpers
  const nameStream = ctx.stream("name");
  const postHumanChance = ctx.stream("post_human")();
  // Name generation: 25% post-human/alien lineage name, 75% worldwide human name
  const personalName = postHumanChance > 0.75
    ? generateAlienName(nameStream)
    : generatePersonName(nameStream);

  // Title generation: prefix x domain
  const titlePrefix = pick(TITLE_PREFIXES, ctx.stream("title_prefix"));
  const titleDomain = pick(TITLE_DOMAINS, ctx.stream("title_domain"));
  const title = `${titlePrefix} of ${titleDomain}`;

  // Tagline from parts
  const taglineLead = pick(TAGLINE_LEADS, ctx.stream("tagline_lead"));
  const taglineTail = pick(TAGLINE_TAILS, ctx.stream("tagline_tail"));
  const tagline = `${taglineLead} ${taglineTail}.`;

  // Civilization context
  const civType = pick(CIVILIZATION_TYPES, ctx.stream("civ_type"));
  const techCore = pick(TECHNOLOGY_CORES, ctx.stream("tech_core"));
  const institution = pick(INSTITUTIONAL_FORMS, ctx.stream("institution"));
  const stellarStation = generatePlaceName(ctx.stream("stellar_place"), "stellar");

  // Role / Occupation: domain (from ALL resolved.interests) x office/scale shaped by resolved.lifePath
  // Pick one primary interest from chosen/resolved interests, weaving in phrases
  const primaryInterest = pick(resolved.interests, ctx.stream("primary_interest"));
  const rolePool = ROLE_DOMAINS_BY_INTEREST[primaryInterest] || ROLE_DOMAINS_BY_INTEREST.science;
  const roleDomain = pick(rolePool, ctx.stream("role_domain"));
  const officePool = OFFICE_BY_LIFEPATH[resolved.lifePath] || OFFICE_BY_LIFEPATH.scholar;
  const office = pick(officePool, ctx.stream("office"));
  const scale = pick(SCALE_PHRASES, ctx.stream("scale"));
  const occupation = `${roleDomain} ${office} (${scale})`;

  // Traits: exactly 4 from all resolved.strengths via STRENGTH_TRAIT_MAP
  const traits: Trait[] = [];
  const strengthPicks = [...resolved.strengths];
  const traitStream = ctx.stream("traits");
  const usedTraitNames = new Set<string>();

  for (let i = 0; i < 4; i++) {
    const sId = strengthPicks[i % strengthPicks.length];
    const items = STRENGTH_TRAIT_MAP[sId] || STRENGTH_TRAIT_MAP.curiosity;
    // pick distinct trait from bank
    let chosenItem = pick(items, traitStream);
    let attempts = 0;
    while (usedTraitNames.has(chosenItem.name) && attempts < 10) {
      chosenItem = pick(items, traitStream);
      attempts++;
    }
    usedTraitNames.add(chosenItem.name);

    const baseLevel =
      sId === "discipline"
        ? axes.stability
        : sId === "courage"
        ? axes.courage
        : sId === "curiosity"
        ? axes.curiosity
        : sId === "empathy"
        ? axes.connection
        : axes.influence;

    const jitter = Math.floor(traitStream() * 14) - 7;
    traits.push({
      name: chosenItem.name,
      description: chosenItem.description,
      level: clamp(baseLevel + jitter, 25, 95),
    });
  }

  // Story beats: origin, turningPoint, today
  // 1. Origin (setting + formative event + awakening)
  const originSettingRaw = pick(ORIGIN_SETTINGS, ctx.stream("origin_setting"));
  const originEventRaw = pick(ORIGIN_FORMATIVE_EVENTS, ctx.stream("origin_event"));
  const originAwakeningRaw = pick(ORIGIN_AWAKENINGS, ctx.stream("origin_awakening"));

  const originVars: Record<string, string> = {
    name: personalName,
    visitor: visitorName,
    station: stellarStation,
    civ: civType.name,
    tech: techCore.focus,
    institution: institution.name,
    interestField: INTEREST_PHRASE[primaryInterest].field,
    lifeRole: LIFE_PATH_PHRASE[resolved.lifePath].role,
  };

  const origin = `${fill(originSettingRaw, originVars)} ${fill(originEventRaw, originVars)} ${fill(originAwakeningRaw, originVars)}`;

  // 2. Turning point: driven by ambition x lifePath x crisis type
  const crisis = pick(CRISIS_TYPES, ctx.stream("crisis_type"));
  const turningActionRaw = pick(
    TURNING_ACTIONS_BY_AMBITION[resolved.ambition] || TURNING_ACTIONS_BY_AMBITION.understand,
    ctx.stream("turning_action")
  );
  const turningResRaw = pick(
    TURNING_RESOLUTIONS_BY_LIFEPATH[resolved.lifePath] || TURNING_RESOLUTIONS_BY_LIFEPATH.scholar,
    ctx.stream("turning_resolution")
  );

  const turningVars: Record<string, string> = {
    name: personalName,
    station: stellarStation,
    crisis: crisis.description,
    ambitionGoal: AMBITION_PHRASE[resolved.ambition].goal,
    lifeManner: LIFE_PATH_PHRASE[resolved.lifePath].manner,
    institution: institution.name,
  };

  const turningPoint = `${crisis.prelude} ${fill(turningActionRaw, turningVars)} ${fill(turningResRaw, turningVars)}`;

  // 3. Today (current duty + quiet intimacy + cosmic horizon)
  const todayDutyRaw = pick(TODAY_DUTIES, ctx.stream("today_duty"));
  const todayIntimacyRaw = pick(TODAY_INTIMACIES, ctx.stream("today_intimacy"));
  const todayHorizonRaw = pick(TODAY_HORIZONS, ctx.stream("today_horizon"));

  const todayVars: Record<string, string> = {
    name: personalName,
    station: stellarStation,
    occupation: occupation.toLowerCase(),
    interestNoun: INTEREST_PHRASE[primaryInterest].noun,
    civ: civType.name,
    universeName: universe.name,
  };

  const today = `${fill(todayDutyRaw, todayVars)} ${fill(todayIntimacyRaw, todayVars)} ${fill(todayHorizonRaw, todayVars)}`;

  // Choices: exactly 3 distinct kinds of events (discovery, institutional confrontation, personal sacrifice)
  const choiceStream = ctx.stream("choices");
  const choices: LifeChoice[] = CHOICE_EVENTS.map((tmpl) => {
    const ageString = `Cycle ${Math.floor(220 + choiceStream() * 380)}`;
    const eventChoiceRaw = pick(tmpl.choices, choiceStream);
    const eventRoadRaw = pick(tmpl.roads, choiceStream);
    const eventConseqRaw = pick(tmpl.consequences, choiceStream);

    const cVars: Record<string, string> = {
      name: personalName,
      station: stellarStation,
      interest: INTEREST_PHRASE[primaryInterest].verb,
      ambitionDrive: AMBITION_PHRASE[resolved.ambition].drive,
    };

    return {
      age: ageString,
      choice: fill(eventChoiceRaw, cVars),
      roadNotTaken: fill(eventRoadRaw, cVars),
      consequence: fill(eventConseqRaw, cVars),
    };
  });

  // World details and description
  const extraDetails: { label: string; value: string }[] = [];
  const extraSlot1 = pick(EXTRA_DETAILS_POOLS.civilization, ctx.stream("extra_civ"));
  const extraSlot2 = pick(EXTRA_DETAILS_POOLS.technology, ctx.stream("extra_tech"));
  const extraSlot3 = pick(EXTRA_DETAILS_POOLS.governance, ctx.stream("extra_gov"));

  extraDetails.push(
    { label: "Civilizational Framework", value: fill(extraSlot1, { civ: civType.name, station: stellarStation }) },
    { label: "Engineering Substrate", value: fill(extraSlot2, { tech: techCore.focus }) },
    { label: "Institutional Order", value: fill(extraSlot3, { institution: institution.name }) }
  );

  const interestPhraseList = listJoin(resolved.interests.map((id) => INTEREST_PHRASE[id].humanDomain));
  const interestDetail = `Organizing post-scarcity computational capacity around ${interestPhraseList}.`;

  const worldDetails = buildWorldDetails("ascendant", universe, condition, interestDetail, extraDetails);
  const universeDescription = buildUniverseDescription(universe, condition, sharedUniverse);

  // Dossier generation:
  // Background: 2-4 sentences
  const backgroundVars = {
    name: personalName,
    station: stellarStation,
    civ: civType.name,
    tech: techCore.focus,
    manner: LIFE_PATH_PHRASE[resolved.lifePath].manner,
    setting: LIFE_PATH_PHRASE[resolved.lifePath].setting,
  };
  const background = `Formed within the orbital precincts of ${stellarStation}, ${personalName} emerged into the ${civType.name}, where daily life is mediated by ${techCore.focus}. Early conditioning in ${LIFE_PATH_PHRASE[resolved.lifePath].setting} forged a habit of ${LIFE_PATH_PHRASE[resolved.lifePath].manner}. Before assuming public office, ${personalName} spent decades mastering the harmonic balance between biological perception and macro-scale telemetry.`;

  // Personality: 2-3 sentences
  const persTemp = pick(PERSONALITY_TEMPERAMENTS, ctx.stream("pers_temp"));
  const persHabit = pick(PERSONALITY_HABITS, ctx.stream("pers_habit"));
  const persConv = pick(PERSONALITY_CONVICTIONS, ctx.stream("pers_conv"));
  const personality = `${fill(persTemp, { name: personalName })} ${fill(persHabit, { name: personalName })} ${fill(persConv, { name: personalName })}`;

  // Skills: 4-6 short phrases, drawing from resolved.interests and resolved.lifePath
  const skillList: string[] = [];
  const skillStream = ctx.stream("skills");
  for (const intId of resolved.interests) {
    const pool = SKILL_BANKS_BY_INTEREST[intId];
    if (pool) {
      skillList.push(pick(pool, skillStream));
    }
  }
  const lifeSkills = SKILL_BANKS_BY_LIFEPATH[resolved.lifePath] || SKILL_BANKS_BY_LIFEPATH.scholar;
  skillList.push(pick(lifeSkills, skillStream));
  skillList.push(pick(lifeSkills, skillStream));
  // Deduplicate and clamp to 4..6
  const uniqueSkills = Array.from(new Set(skillList));
  while (uniqueSkills.length < 4) {
    uniqueSkills.push(pick(SKILL_BANKS_BY_LIFEPATH.founder, skillStream));
  }
  const skills = uniqueSkills.slice(0, 5);

  // Motivations: 2-3 phrases/sentences from ambition
  const ambPhrases = AMBITION_PHRASE[resolved.ambition];
  const motivations = [
    `To advance ${ambPhrases.drive} across multi-generational stellar projects.`,
    `To ensure civilizational power remains accountable through ${ambPhrases.verbing}.`,
    `To safeguard emerging conscious minds from the systemic stagnation of post-scarcity comfort.`,
  ];

  // Relationships: exactly 3 distinct roles (AI steward, rival faction lead, companion/descendant)
  const relMentor = pick(RELATIONSHIP_MENTORS, ctx.stream("rel_mentor"));
  const relRival = pick(RELATIONSHIP_RIVALS, ctx.stream("rel_rival"));
  const relComp = pick(RELATIONSHIP_COMPANIONS, ctx.stream("rel_comp"));

  const relationships: Relationship[] = [
    {
      who: relMentor.who,
      role: relMentor.role,
      bond: fill(relMentor.bond, { name: personalName, station: stellarStation, occupation: occupation.toLowerCase() }),
    },
    {
      who: relRival.who,
      role: relRival.role,
      bond: fill(relRival.bond, { name: personalName, institution: institution.name }),
    },
    {
      who: relComp.who,
      role: relComp.role,
      bond: fill(relComp.bond, { name: personalName }),
    },
  ];

  // Achievements: 2-3 sentences
  const achPool = ACHIEVEMENT_POOLS_BY_AMBITION[resolved.ambition] || ACHIEVEMENT_POOLS_BY_AMBITION.understand;
  const achStream = ctx.stream("achievements");
  const achPicks = pickDistinct(achPool, 3, achStream);
  const achievements = achPicks.map((ach) => fill(ach, { name: personalName, station: stellarStation }));

  // Failures: exactly 2 sentences
  const failStream = ctx.stream("failures");
  const failPicks = pickDistinct(FAILURE_POOLS, 2, failStream);
  const failures = failPicks.map((fail) => fill(fail, { name: personalName }));

  // Dilemma: 2-3 sentences, an open tension between progress and ethical limits
  const dilProg = pick(DILEMMA_PROGRESS_POOLS, ctx.stream("dil_prog"));
  const dilTens = pick(DILEMMA_TENSION_POOLS, ctx.stream("dil_tens"));
  const dilemma = `${fill(dilProg, { name: personalName, station: stellarStation })} ${fill(dilTens, { name: personalName, civ: civType.name })}`;

  // Trajectory: nearFuture (1-3 sentences) and longArc (1-3 sentences)
  const nearRaw = pick(TRAJECTORY_NEAR_POOLS, ctx.stream("near_future"));
  const longRaw = pick(TRAJECTORY_LONG_POOLS, ctx.stream("long_arc"));
  const trajectory = {
    nearFuture: fill(nearRaw, { name: personalName, station: stellarStation }),
    longArc: fill(longRaw, { name: personalName, universeName: universe.name }),
  };

  // Influence: baseline lead + 2-3 specific sentences linking visitor answers to this persona
  const baseInfluence = influenceLead(ctx);
  const specificInfluence: string[] = [];

  // Concrete interest influence
  const interestNames = listJoin(resolved.interests.map((i) => INTEREST_PHRASE[i].noun));
  specificInfluence.push(
    `Your engagement with ${interestNames} directed this persona toward the high-level role of ${occupation.toLowerCase()}, applying theoretical mastery to immense engineering structures.`
  );

  // Concrete ambition & life path influence
  specificInfluence.push(
    `Because your ambition centered on "${AMBITION_PHRASE[resolved.ambition].goal}", the turning point confronted ${personalName} with ${crisis.description.toLowerCase()}, resolved through ${LIFE_PATH_PHRASE[resolved.lifePath].manner}.`
  );

  // Strengths influence
  const strengthAdjs = listJoin(resolved.strengths.map((s) => STRENGTH_PHRASE[s].adj));
  specificInfluence.push(
    `Operating as a ${strengthAdjs} steward, ${personalName} navigates the core dilemma between civilizational progress and ethical containment.`
  );

  const influence = [...baseInfluence, ...specificInfluence];

  const dossier: Dossier = {
    background,
    personality,
    skills,
    motivations,
    relationships,
    achievements,
    failures,
    dilemma,
    trajectory,
    influence,
  };

  return {
    name: personalName,
    title,
    tagline,
    occupation,
    traits,
    story: { origin, turningPoint, today },
    choices,
    worldDetails,
    universeDescription,
    dossier,
  };
}
